import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const PageContainer = styled.div`
  min-height: 100vh;
  background: ${colors.porcelain};
`;

export const HeroSection = styled.section`
  position: relative;
  padding: 180px 40px 120px;
  background: linear-gradient(
    135deg,
    ${colors.deepCharcoal} 0%,
    ${colors.frenchBlue} 100%
  );
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  @media (max-width: 1024px) {
    padding: 160px 30px 100px;
  }

  @media (max-width: 768px) {
    padding: 140px 20px 80px;
  }
`;

export const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 50%,
      rgba(240, 244, 239, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(240, 244, 239, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 40% 20%,
      rgba(240, 244, 239, 0.02) 0%,
      transparent 50%
    );
  pointer-events: none;
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

export const HeroTitle = styled(motion.h1)`
  font-size: 84px;
  font-weight: 900;
  color: ${colors.porcelain};
  margin: 0 0 24px;
  letter-spacing: -0.03em;
  font-family: ${fonts.headline};
  line-height: 1.1;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));

  @media (max-width: 1024px) {
    font-size: 64px;
  }

  @media (max-width: 768px) {
    font-size: 48px;
    margin-bottom: 16px;
  }
`;

export const HeroSubtitle = styled(motion.h2)`
  font-size: 24px;
  font-weight: 500;
  color: ${colors.porcelain};
  opacity: 0.9;
  margin: 0 0 32px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 24px;
  }
`;

export const HeroDescription = styled(motion.p)`
  font-size: 18px;
  font-weight: 400;
  color: ${colors.deepCharcoal};
  opacity: 0.8;
  margin: 0;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  font-family: ${fonts.body};

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const CategoriesSection = styled.section`
  padding: 120px 40px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    padding: 120px 30px;
  }

  @media (max-width: 768px) {
    padding: 120px 20px;
  }
`;

export const SectionSubtitle = styled(motion.div)`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 3px;
  text-align: center;
  margin-bottom: 16px;
  font-family: ${fonts.body};
`;

export const SectionTitle = styled(motion.h2)`
  font-size: 64px;
  font-weight: 900;
  color: ${colors.sapphire};
  text-align: center;
  margin: 0 0 20px;
  letter-spacing: -0.03em;
  font-family: ${fonts.headline};
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    border-radius: 2px;
  }

  @media (max-width: 1024px) {
    font-size: 52px;
    margin-bottom: 6px;
  }

  @media (max-width: 768px) {
    font-size: 40px;
    margin-bottom: 16px;
  }
`;

export const CategoriesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  margin-top: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 40px;
  }
`;

export const CategoryCard = styled(motion(Link))`
  position: relative;
  background: transparent;
  overflow: visible;
  text-decoration: none;
  color: inherit;
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
  }
`;

export const CategoryImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  flex-shrink: 0;
`;

export const CategoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1);

  ${CategoryCard}:hover & {
    transform: scale(1.15);
  }
`;

export const CategoryOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(17, 24, 39, 0.6) 0%,
    rgba(17, 24, 39, 0.3) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${CategoryCard}:hover & {
    opacity: 1;
  }
`;

export const CategoryArrow = styled(motion.span)`
  font-size: 36px;
  color: #ffffff;
  font-weight: 300;
  transform: translateX(-20px);
  transition: transform 0.3s ease;

  ${CategoryCard}:hover & {
    transform: translateX(0);
  }
`;

export const CategoryName = styled.div`
  padding: 24px 0px;
  font-size: 20px;
  color: gray;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${fonts.body};
  transition: color 0.3s ease;
  position: relative;
  z-index: 1;

  ${CategoryCard}:hover & {
    color: ${colors.sapphire};
  }

  @media (max-width: 1024px) {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    padding: 20px 0px;
    font-size: 16px;
  }
`;
